<template>
  <!-- Edit Account -->
  <SmDialog
    v-model:visibility="visibilityEditDialog"
    size="medium"
    :title="application?.display_name || application?.software_name">
    <AccountLicenseEditDialog
      v-if="application && person"
      :people="[person]"
      :application="application"
      :license="account"
      :software-licenses="softwareLicenses"
      @close="visibilityEditDialog = false"
      @edited-license="refetchAccount()" />
  </SmDialog>

  <!-- Deleting a single license -->
  <DeleteConfirmDialog
    v-model:visible="confirmDialogVisible"
    :title="i18n.t('deleteLicence')"
    :sub-title="
      i18n.t('views.applicationDetails.deleteLicenseOf', {
        email: account.email,
      })
    "
    @cancel="confirmDialogVisible = false"
    @delete="deleteAccount()" />

  <!-- Drawer -->
  <SmDrawer v-model:visibility="visibility" :title="i18n.t('account')">
    <template #close>
      <div
        v-if="application?.information.implements_delete_user_method"
        class="dropdown-item delete-user"
        @click="confirmDialogVisible = !confirmDialogVisible">
        <Trash class="hover:text-magenta" :size="20" />
      </div>
      <SmButtonClose @close="visibility = false" />
    </template>

    <div class="flex flex-col gap-7">
      <!--License Information-->
      <div class="license-information gap-4" style="margin-top: 10px">
        <ApplicationIcon
          v-if="application"
          :size="40"
          :application-name="application.display_name"
          :application-type="application.type" />

        <div class="license-information__text">
          <h3 style="margin-top: 0">
            {{ application?.display_name || application?.software_name }}
          </h3>
          <span
            v-if="account.email"
            class="license-information__text__mail text-contrast-muted"
            >{{ account.email }}</span
          >
          <AccountStatusSmall v-if="account.status" :status="account.status" />
        </div>
      </div>

      <!-- License Model Display and Select-->
      <div v-loading="applicationLoading">
        <div class="flex w-full items-baseline justify-between">
          <h2 class="mb-2">{{ i18n.t('licenses') }}</h2>
          <SmTooltip>
            <div style="cursor: pointer" @click="openEditDialog()">
              <SquarePen :size="20" />
            </div>
            <template #content>
              <span>
                {{ i18n.t('editLicense') }}
              </span>
            </template>
          </SmTooltip>
        </div>

        <!-- Empty state -->
        <div
          v-if="
            !account ||
            !account.software_licenses ||
            Object.keys(account.software_licenses).length == 0
          ">
          <p>
            {{
              i18n.t('noLicenses', {
                software:
                  application?.display_name || application?.software_name,
              })
            }}
          </p>
        </div>

        <div
          v-else-if="softwareLicensesLoading"
          class="flex flex-col gap-3"
          aria-busy="true">
          <div
            v-for="skeletonRow in licenseSkeletonRows"
            :key="skeletonRow"
            class="flex items-center gap-3">
            <SmSkeleton height="30px" width="30px" />
            <div class="flex w-full flex-col gap-2">
              <SmSkeleton height="14px" width="40%" />
              <SmSkeleton height="12px" width="25%" />
            </div>
          </div>
        </div>

        <!-- Not empty state (content) -->
        <div v-else class="flex flex-col gap-3">
          <div
            v-for="license in accountLicenses"
            :key="license._id"
            class="flex items-center gap-3">
            <el-image
              v-if="license.icon"
              :src="`https://logos.saasmetrix.io/${application?.software_name}/${license.icon}.png`"
              class="h-[30px] w-[30px]">
              <template #error>
                <div class="flex h-full w-full items-center justify-center">
                  <ImageOff :size="20" />
                </div>
              </template>
            </el-image>

            <div class="flex w-full items-center gap-3">
              <div class="flex flex-col">
                <p class="font-medium">
                  {{ license.name }}
                </p>
                <span class="text-xs text-contrast-muted">
                  {{ i18n.t('views.dashboard.lastActivity.title') }}:
                  {{ lastLoginText }}
                </span>
                <span
                  v-if="license.subtitle"
                  class="text-sm text-contrast-muted">
                  {{ license.subtitle }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistik -->
      <div
        v-if="
          account &&
          account.statistics &&
          (account.statistics.active_user_detail ||
            (account.statistics as any).miro_events)
        "
        class="license-model">
        <div class="statistics">
          <h2>{{ i18n.t('drawers.licenceDetailDrawer.statistics') }}</h2>
          <div @click="refreshStatistics()" class="cursor-pointer">
            <RotateCcw :size="20" />
          </div>
        </div>
        <div class="statistics-content">
          <h3>{{ i18n.t('lastUsage') }}</h3>
          <span></span>

          <el-table
            class="statistic-table mt-2 w-full"
            :data="usageData"
            border>
            <el-table-column
              prop="service"
              :label="isMiroApp ? i18n.t('event') : i18n.t('service')" />
            <el-table-column
              prop="lastActivity"
              :label="i18n.t('views.dashboard.lastActivity.title')" />
            <el-table-column
              prop="status"
              :label="i18n.t('activityLast70Days')">
              <template #default="{ row }">
                <div class="flex items-center gap-2">
                  <span
                    class="indicator-dot shrink-0"
                    :class="row.status.color"></span>
                  <span>{{ row.status.text }}</span>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div v-else-if="!account.statistics" class="license-model">
        <span>{{
          i18n.t('drawers.licenceDetailDrawer.noStatisticsAvailable')
        }}</span>
      </div>

      <!-- User Daten -->
      <div v-loading="personLoading" class="user license-model">
        <h2 class="mb-2">{{ i18n.t('person') }}</h2>
        <div class="user__content license-model__content">
          <span>{{ i18n.t('general.name') }}:</span>
          <TextMultiline :text="person?.name ?? ''" />

          <span
            >{{
              i18n.t('drawers.userDetailDrawer.informationCard.items.email')
            }}:</span
          >

          <TextMultiline :text="person?.email ?? ''" />

          <span>
            {{
              i18n.t('drawers.userDetailDrawer.informationCard.items.phone')
            }}:
          </span>
          <TextMultiline :text="person?.phone ?? ''" />
        </div>
      </div>

      <!-- Optimierungen -->
      <div v-loading="optimizationsLoading">
        <h2 class="mb-4">{{ i18n.t('sidebar.optimizations') }}</h2>

        <div v-if="optimizations.length > 0" class="flex flex-col gap-3">
          <template
            v-for="optimization in optimizations"
            :key="optimization._id">
            <OptimizationPreview
              :optimization="optimization"
              :application="true" />
          </template>
        </div>
        <div v-else class="flex flex-col gap-3">
          <p>{{ i18n.t('drawers.licenceDetailDrawer.noOptimizations') }}</p>
        </div>
      </div>

      <!-- Notes -->
      <div class="license-model">
        <div class="mb-2 flex w-full items-baseline justify-between gap-3">
          <h2 class="mb-2">{{ i18n.t('notes') }}</h2>
          <SmButton
            size="smaller"
            :loading="savingNotes"
            :disabled="!notesDirty"
            @click="saveNotes()">
            {{ i18n.t('save') }}
          </SmButton>
        </div>

        <SmInput
          v-model="accountNotesDraft"
          design="form"
          type="area"
          :placeholder="i18n.t('notesPlaceholder')"
          class="w-full" />
      </div>
    </div>
  </SmDrawer>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'

  import {
    Account,
    LicenceOut,
    SoftwareLicense,
    SoftwareLicensesService,
    SoftwareOut,
    SoftwareService,
  } from '@/client'
  import { ApplicationStore } from '@/stores/application/application.store'
  import {
    EnrichedOptimizations,
    OptimizationStore,
  } from '@/stores/optimization/optimization.store'
  import { displayDate } from '@/utils/formatter/date'
  import dayjs from 'dayjs'
  import utc from 'dayjs/plugin/utc'
  import timezonePlugin from 'dayjs/plugin/timezone'
  import customParseFormat from 'dayjs/plugin/customParseFormat'
  import { AccountStore } from '@/stores/account/account.store'
  import { editToast, sendToast } from '../sm/SmNotification'
  import ApplicationIcon from '@/components/applications/ApplicationIcon.vue'
  import TextMultiline from '@/components/sm/TextMultiline.vue'
  import { PersonStore } from '@/stores/person/person.store'
  import { ImageOff, RotateCcw, SquarePen, Trash } from 'lucide-vue-next'
  import { resolveSoftwareLicenseName } from '@/utils/licenseDisplay'

  const i18n = useI18n()
  dayjs.extend(utc)
  dayjs.extend(timezonePlugin)
  dayjs.extend(customParseFormat)

  const props = defineProps({
    account: {
      type: Object as PropType<LicenceOut>,
      default: null,
    },
  })

  const emit = defineEmits<{
    (e: 'license-changed'): void
  }>()

  const timezone = dayjs.tz.guess()

  // Visibility
  const visibility = defineModel('visibility', {
    type: Boolean,
    required: true,
  })

  // ---------------------------------------------------------------------------
  // Load Data
  // ---------------------------------------------------------------------------

  const account = ref({ ...props.account })
  const accountNotesDraft = ref(props.account?.notes || '')
  const savingNotes = ref(false)

  const notesDirty = computed(function () {
    const currentNotes = account.value?.notes || ''
    return accountNotesDraft.value !== currentNotes
  })

  watch(
    () => props.account,
    function (updatedAccount) {
      if (!updatedAccount) return
      account.value = { ...updatedAccount }
      accountNotesDraft.value = updatedAccount?.notes || ''
      application.value = undefined
      person.value = null
      softwareLicenses.value = []
      loadApplication()
      loadPerson()
    }
  )

  function refetchAccount() {
    AccountStore.getAccountById(props.account._id!)
      .then((updatedAccount) => {
        if (!updatedAccount) {
          console.error('Account not found')
          return
        }
        account.value = updatedAccount
        accountNotesDraft.value = updatedAccount.notes || ''
      })
      .catch((error) => {
        console.error('Failed to refetch account:', error)
      })

    emit('license-changed')
  }

  async function saveNotes() {
    if (!account.value?._id || !notesDirty.value) return

    savingNotes.value = true

    try {
      const response = await AccountStore.updateAccount(
        account.value._id,
        {
          notes: accountNotesDraft.value || null,
        },
        {
          successToastTitle:
            'notifications.applicationsDetailView.licence.notesSaved.title',
        }
      )

      if (!response.error && response.data) {
        account.value = response.data
        accountNotesDraft.value = response.data.notes || ''
        emit('license-changed')
      }
    } finally {
      savingNotes.value = false
    }
  }

  onMounted(() => {
    loadApplication()
    loadPerson()
  })

  const applicationLoading = ref(false)
  const application = ref<SoftwareOut>()

  async function loadApplication(forceRefreshFromApi: boolean = false) {
    if (application.value && !forceRefreshFromApi) {
      return application.value
    }

    applicationLoading.value = true
    try {
      const response = await ApplicationStore.getApplicationById(
        account.value.software_id,
        !forceRefreshFromApi
      )
      if (response === null) throw new Error('Application not found')

      application.value = response
      await loadLicenses(response)

      return response
    } finally {
      applicationLoading.value = false
    }
  }

  const optimizationsLoading = ref(false)

  const { data: optimizations } =
    OptimizationStore.useOptimizations<EnrichedOptimizations>(
      {
        account_ids: [props.account._id],
        hidden: false,
        deprecated: false,
      },
      {},
      undefined,
      true
    )

  const person = ref<Account | null>(null)
  const personLoading = ref(false)

  async function loadPerson(forceRefreshFromApi: boolean = false) {
    personLoading.value = true

    try {
      let response: Account | null = null

      if (account.value.account_id) {
        response = await PersonStore.getPersonById(account.value.account_id)
      }

      if (!response) {
        response = await PersonStore.getPersonByEmail(account.value.email)
      }

      if (!response && forceRefreshFromApi) {
        await PersonStore.refreshPersonCacheFromApi()

        if (account.value.account_id) {
          response = await PersonStore.getPersonById(account.value.account_id)
        }

        if (!response) {
          response = await PersonStore.getPersonByEmail(account.value.email)
        }
      }

      person.value = response
      return response
    } finally {
      personLoading.value = false
    }
  }

  const softwareLicenses = ref<SoftwareLicense[]>([])
  const softwareLicensesLoading = ref(false)

  const softwareLicensesById = computed(function () {
    const map = new Map<string, SoftwareLicense>()
    softwareLicenses.value.forEach(function (license) {
      map.set(license._id, license)
    })
    return map
  })

  interface AccountLicenseDisplay {
    _id: string
    name: string
    subtitle?: string | null
    icon?: string | null
  }

  const licenseSkeletonRows = computed(function (): number[] {
    const assignedLicensesCount = Object.keys(
      account.value?.software_licenses || {}
    ).length
    const rowsCount = Math.max(assignedLicensesCount, 1)

    return Array.from({ length: rowsCount }, function (_, index) {
      return index
    })
  })

  const accountLicenses = computed(function (): AccountLicenseDisplay[] {
    if (!account.value || !account.value.software_licenses) {
      return []
    }

    return Object.keys(account.value.software_licenses).map(
      function (licenseId) {
        const fallback = softwareLicensesById.value.get(licenseId)

        return {
          _id: licenseId,
          name: resolveSoftwareLicenseName(
            licenseId,
            softwareLicensesById.value
          ),
          subtitle: fallback?.subtitle || null,
          icon: fallback?.icon || null,
        }
      }
    )
  })

  async function loadLicenses(application: SoftwareOut) {
    softwareLicensesLoading.value = true
    try {
      softwareLicenses.value =
        await SoftwareLicensesService.getSoftwareLicensesForSoftwareApiV1SoftwareSoftwareSoftwareIdLicensesGet(
          { softwareId: application._id }
        )
    } finally {
      softwareLicensesLoading.value = false
    }
  }

  // ---------------------------------------------------------------------------
  // Handle Dialogs
  // ---------------------------------------------------------------------------

  const visibilityEditDialog = ref(false)
  const confirmDialogVisible = ref(false)
  const editDialogLoading = ref(false)

  async function openEditDialog() {
    if (editDialogLoading.value) return

    editDialogLoading.value = true

    try {
      const loadedApplication = await loadApplication(true)
      if (!loadedApplication) {
        console.error(
          'Failed to open account edit dialog: application not loaded'
        )
        sendToast(i18n.t('error.general.unknown_error.message'), '', 'error')
        return
      }

      const loadedPerson = await loadPerson(true)
      if (!loadedPerson) {
        console.error('Failed to open account edit dialog: person not loaded')
        sendToast(i18n.t('error.general.unknown_error.message'), '', 'error')
        return
      }

      visibilityEditDialog.value = true
    } catch (error) {
      console.error('Failed to open edit dialog:', error)
      sendToast(i18n.t('error.general.unknown_error.message'), '', 'error')
    } finally {
      editDialogLoading.value = false
    }
  }

  // ---------------------------------------------------------------------------
  // Execute Actions
  // ---------------------------------------------------------------------------

  function deleteAccount() {
    AccountStore.deleteAccount(props.account._id!).then(() => {
      sendToast(
        i18n.t(
          'notifications.applicationsDetailView.licence.deleteSuccess.title'
        ),
        '',
        'success'
      )
      visibility.value = false
    })
  }

  function refreshStatistics() {
    const notificationId = sendToast(
      i18n.t('notification.loading'),
      undefined,
      'loading'
    )
    SoftwareService.fetchLicenceStatisticsApiV1SoftwareSoftwareSoftwareIdFetchLicencesStatisticsPost(
      {
        softwareId: props.account.software_id,
        subAccountId: props.account.sub_account_id,
      }
    ).finally(() => {
      editToast(notificationId, {
        title: i18n.t('notification.loadingSuccess'),
        type: 'success',
      })
    })
  }

  function getNormalizedDate(input: any) {
    if (!input) return null
    if (typeof input === 'object' && input.$date) return input.$date
    return input
  }

  function isActiveWithin(
    lastActivityDate: string | null | undefined
  ): boolean {
    const dateStr = getNormalizedDate(lastActivityDate)
    if (!dateStr || dateStr === '-') return false

    const lastActivity = dayjs(dateStr).tz(timezone)
    if (!lastActivity.isValid()) return false

    const diffDays = dayjs().tz(timezone).diff(lastActivity, 'day')

    return diffDays <= 70
  }

  function formatLastActivityDate(input: string | null | undefined): string {
    const dateStr = getNormalizedDate(input)
    if (!dateStr || dateStr === '-' || dateStr.trim() === '') return '-'

    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return '-'

    return displayDate(date, { dateStyle: 'short' })
  }

  function getActivityStatus(lastActivity: string | null | undefined) {
    const active = isActiveWithin(lastActivity)
    if (active) {
      return { text: i18n.t('active'), color: 'bg-primary' }
    } else {
      return { text: i18n.t('inactive'), color: 'bg-yellow' }
    }
  }

  function getWebMobileStatus(appDetail: any) {
    if (!appDetail) return { text: i18n.t('inactive'), color: 'bg-yellow' }

    const date = appDetail.lastActivityDate
    const detail = appDetail.details?.[0] || {}

    const hasWebMobile =
      detail.mobile ||
      detail.web ||
      detail.outlookMobile ||
      detail.wordMobile ||
      detail.excelMobile ||
      detail.powerPointMobile ||
      detail.oneNoteMobile ||
      detail.teamsMobile ||
      detail.outlookWeb ||
      detail.wordWeb ||
      detail.excelWeb ||
      detail.powerPointWeb ||
      detail.oneNoteWeb ||
      detail.teamsWeb

    if (hasWebMobile && isActiveWithin(date)) {
      return { text: i18n.t('active'), color: 'bg-primary', status: 'active' }
    }
    return { text: i18n.t('inactive'), color: 'bg-yellow', status: 'inactive' }
  }

  function getDesktopStatus(appDetail: any) {
    if (!appDetail) return { text: i18n.t('inactive'), color: 'bg-yellow' }

    const date = appDetail.lastActivityDate
    const detail = appDetail.details?.[0] || {}

    const hasDesktop =
      detail.windows ||
      detail.mac ||
      detail.outlookWindows ||
      detail.wordWindows ||
      detail.excelWindows ||
      detail.powerPointWindows ||
      detail.oneNoteWindows ||
      detail.teamsWindows ||
      detail.outlookMac ||
      detail.wordMac ||
      detail.excelMac ||
      detail.powerPointMac ||
      detail.oneNoteMac ||
      detail.teamsMac

    if (hasDesktop && isActiveWithin(date)) {
      return { text: i18n.t('active'), color: 'bg-primary', status: 'active' }
    }
    return { text: i18n.t('inactive'), color: 'bg-yellow', status: 'inactive' }
  }

  const isMiroApp = computed(() =>
    application.value?.software_name?.toLowerCase().includes('miro')
  )

  const usageData = computed(() => {
    if (isMiroApp.value) return miroUsageData.value
    return lastUsageData.value
  })

  const miroUsageData = computed(() => {
    const stats = (account.value?.statistics as any) || {}
    const events = stats.miro_events || []
    const eventList = Array.isArray(events) ? events : [events]

    const grouped = eventList.reduce((acc: Record<string, any>, curr: any) => {
      if (!curr?.event) return acc
      const rawDate = getNormalizedDate(curr.createdAt)

      if (
        !acc[curr.event] ||
        dayjs(rawDate).isAfter(
          dayjs(getNormalizedDate(acc[curr.event].rawDate))
        )
      ) {
        acc[curr.event] = {
          service: curr.event
            .split('_')
            .map((w: any) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '),
          rawDate: curr.createdAt,
          lastActivity: formatLastActivityDate(curr.createdAt),
        }
      }
      return acc
    }, {})

    return Object.values(grouped)
      .map((e: any) => ({
        ...e,
        status: getActivityStatus(e.rawDate),
      }))
      .sort((a: any, b: any) => {
        const dateA = dayjs(getNormalizedDate(a.rawDate))
        const dateB = dayjs(getNormalizedDate(b.rawDate))
        return dateB.isAfter(dateA) ? 1 : -1
      })
  })

  const lastUsageData = computed(() => {
    const stats = account.value?.statistics || {}

    return [
      {
        service: i18n.t('teams'),
        lastActivity: formatLastActivityDate(
          stats.active_user_detail?.teamsLastActivityDate
        ),
        status: getActivityStatus(
          stats.active_user_detail?.teamsLastActivityDate
        ),
      },
      {
        service: i18n.t('exchange'),
        lastActivity: formatLastActivityDate(
          stats.active_user_detail?.exchangeLastActivityDate
        ),
        status: getActivityStatus(
          stats.active_user_detail?.exchangeLastActivityDate
        ),
      },
      {
        service: i18n.t('oneDrive'),
        lastActivity: formatLastActivityDate(
          stats.active_user_detail?.oneDriveLastActivityDate
        ),
        status: getActivityStatus(
          stats.active_user_detail?.oneDriveLastActivityDate
        ),
      },
      {
        service: i18n.t('sharePoint'),
        lastActivity: formatLastActivityDate(
          stats.active_user_detail?.sharePointLastActivityDate
        ),
        status: getActivityStatus(
          stats.active_user_detail?.sharePointLastActivityDate
        ),
      },
      {
        service: i18n.t('skypeForBusiness'),
        lastActivity: formatLastActivityDate(
          stats.active_user_detail?.skypeForBusinessLastActivityDate
        ),
        status: getActivityStatus(
          stats.active_user_detail?.skypeForBusinessLastActivityDate
        ),
      },
      {
        service: i18n.t('yammer'),
        lastActivity: formatLastActivityDate(
          stats.active_user_detail?.yammerLastActivityDate
        ),
        status: getActivityStatus(
          stats.active_user_detail?.yammerLastActivityDate
        ),
      },
      {
        service: i18n.t('windows'),
        lastActivity: formatLastActivityDate(
          stats.windows_sign_in?.createdDateTime
        ),
        status: getActivityStatus(stats.windows_sign_in?.createdDateTime),
      },
      {
        service: i18n.t('enterpriseApps'),
        status: getDesktopStatus(stats.app_user_detail),
        lastActivity: (() => {
          const desktopStatus = getDesktopStatus(stats.app_user_detail)
          return desktopStatus.status === 'active'
            ? formatLastActivityDate(stats.app_user_detail?.lastActivityDate)
            : '-'
        })(),
      },
      {
        service: i18n.t('webMobileAccess'),
        status: getWebMobileStatus(stats.app_user_detail),
        lastActivity: (() => {
          const webMobileStatus = getWebMobileStatus(stats.app_user_detail)
          return webMobileStatus.status === 'active'
            ? formatLastActivityDate(stats.app_user_detail?.lastActivityDate)
            : '-'
        })(),
      },
      {
        service: i18n.t('copilot'),
        lastActivity: formatLastActivityDate(
          stats.copilot_user_detail?.lastActivityDate
        ),
        status: getActivityStatus(stats.copilot_user_detail?.lastActivityDate),
      },
      /*
      {
        service: i18n.t('copilotChat'),
        lastActivity: formatLastActivityDate(
          stats.copilot_user_detail?.copilotChatLastActivityDate
        ),
        status: getActivityStatus(
          stats.copilot_user_detail?.copilotChatLastActivityDate
        ),
      },
      {
        service: i18n.t('copilotTeams'),
        lastActivity: formatLastActivityDate(
          stats.copilot_user_detail?.microsoftTeamsCopilotLastActivityDate
        ),
        status: getActivityStatus(
          stats.copilot_user_detail?.microsoftTeamsCopilotLastActivityDate
        ),
      },
      {
        service: i18n.t('copilotWord'),
        lastActivity: formatLastActivityDate(
          stats.copilot_user_detail?.wordCopilotLastActivityDate
        ),
        status: getActivityStatus(
          stats.copilot_user_detail?.wordCopilotLastActivityDate
        ),
      },
      {
        service: i18n.t('copilotExcel'),
        lastActivity: formatLastActivityDate(
          stats.copilot_user_detail?.excelCopilotLastActivityDate
        ),
        status: getActivityStatus(
          stats.copilot_user_detail?.excelCopilotLastActivityDate
        ),
      },
      {
        service: i18n.t('copilotPowerPoint'),
        lastActivity: formatLastActivityDate(
          stats.copilot_user_detail?.powerPointCopilotLastActivityDate
        ),
        status: getActivityStatus(
          stats.copilot_user_detail?.powerPointCopilotLastActivityDate
        ),
      },
      {
        service: i18n.t('copilotOutlook'),
        lastActivity: formatLastActivityDate(
          stats.copilot_user_detail?.outlookCopilotLastActivityDate
        ),
        status: getActivityStatus(
          stats.copilot_user_detail?.outlookCopilotLastActivityDate
        ),
      },
      {
        service: i18n.t('copilotOneNote'),
        lastActivity: formatLastActivityDate(
          stats.copilot_user_detail?.oneNoteCopilotLastActivityDate
        ),
        status: getActivityStatus(
          stats.copilot_user_detail?.oneNoteCopilotLastActivityDate
        ),
      },
      {
        service: i18n.t('copilotLoop'),
        lastActivity: formatLastActivityDate(
          stats.copilot_user_detail?.loopCopilotLastActivityDate
        ),
        status: getActivityStatus(
          stats.copilot_user_detail?.loopCopilotLastActivityDate
        ),
      },
      */
    ]
  })

  const lastLoginText = computed(() => {
    const lastLogin = account.value?.last_login_time

    if (!lastLogin || lastLogin.trim() === '') return 'N/A'

    const formatted = formatLastActivityDate(lastLogin)
    return formatted === '-' ? 'N/A' : formatted
  })
</script>

<style lang="scss" scoped>
  h1,
  h2 {
    line-height: 1;
  }

  // License information
  .license-information {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    width: 100%;

    &__text {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;

      &__mail {
        margin-bottom: 8px;
      }
    }
  }

  // License model
  .license-model {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;

    &__content {
      display: grid;
      grid-template-columns: minmax(70px, auto) 1fr;
      gap: 2px 12px;
      width: 100%;
      align-items: center;
      justify-items: flex-start;
    }
  }

  .license__item {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-top: -10px;
    transition: background-color 0.2s ease;
    border-bottom: 1px solid #e5e5e5;
    border-radius: 10px;

    &:last-child {
      border-bottom: none;
    }

    &__name {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: flex-start;
    }
  }

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .optimizations-text {
    display: flex;
    justify-content: flex-start;
  }

  .license-model-tooltip-content {
    display: grid;
    grid-template-columns: auto auto;
    gap: 0 10px;
  }

  .statistics {
    display: flex;
    width: 100%;
    justify-content: space-between;
    flex-direction: row;
    align-items: baseline;
  }

  .statistics-content {
    display: grid;
    grid-template-columns: auto auto;
    gap: 0 10px;
    width: 100%;
    text-align: left;
  }

  .statistic-table {
    border-radius: 0 !important;
  }

  .indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
  .bg-primary {
    background-color: var(--sm-green);
  }
  .bg-yellow {
    background-color: var(--el-color-warning);
  }
</style>
